import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCookieConsent } from '../../contexts/CookieConsentContext';
import { motion } from 'framer-motion';
import { ReceevitLogoCompact } from '../common/ReceevitLogo';
import api from '../../utils/api';
import { 
  LayoutDashboard, 
  Users, 
  CreditCard, 
  FileText, 
  
  UserCheck, 
  Shield, 
  Settings, 
  LogOut,
  Menu,
  X,
  User,
  Bell,
} from 'lucide-react';

const StaffLayout = ({ children }) => {
  const { openPreferences } = useCookieConsent();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  // Refs for dropdowns
  const notificationsRef = useRef(null);
  const settingsRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
      if (settingsRef.current && !settingsRef.current.contains(event.target)) {
        setSettingsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return timestamp.toLocaleDateString();
  };

  // Get priority color for notification
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Get type icon for notification
  const getTypeIcon = (type) => {
    switch (type) {
      case 'user': return '👤';
      case 'subscription': return '💳';
      case 'billing': return '💰';
      case 'system': return '⚙️';
      case 'security': return '🔒';
      case 'maintenance': return '🔧';
      default: return '🔔';
    }
  };

  // Mark notification as read
  const markNotificationAsRead = async (notificationId) => {
    try {
      // Call backend API to mark as read
      const response = await api.put(`/api/notifications/${notificationId}/read`);
      
      if (response.data.success) {
        // Update local state
        setNotifications(prev => 
          prev.map(n => 
            n.id === notificationId ? { ...n, isRead: true } : n
          )
        );
        
        // Update notification count
        setNotificationCount(prev => Math.max(0, prev - 1));
        
        console.log('✅ Notification marked as read:', notificationId);
      } else {
        console.error('❌ Backend error marking notification as read:', response.data.error);
      }
    } catch (error) {
      console.error('❌ Error marking notification as read:', error);
      // Fallback to local state update
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, isRead: true } : n
        )
      );
      setNotificationCount(prev => Math.max(0, prev - 1));
    }
  };

  // Delete individual notification
  const deleteNotification = async (notificationId) => {
    try {
      // Call backend API to delete notification
      const response = await api.delete(`/api/notifications/${notificationId}`);
      
      if (response.data.success) {
        // Remove from local state
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        
        // Update notification count if it was unread
        const deletedNotification = notifications.find(n => n.id === notificationId);
        if (deletedNotification && !deletedNotification.isRead) {
          setNotificationCount(prev => Math.max(0, prev - 1));
        }
        
        console.log('✅ Notification deleted:', notificationId);
      } else {
        console.error('❌ Backend error deleting notification:', response.data.error);
      }
    } catch (error) {
      console.error('❌ Error deleting notification:', error);
      // Fallback to local state update
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    }
  };

  // Clear all notifications
  const clearAllNotifications = async () => {
    try {
      // Call backend API to clear all notifications
      const response = await api.delete('/api/notifications/staff/clear-all');
      
      if (response.data.success) {
        // Clear local state
        setNotifications([]);
        setNotificationCount(0);
        
        console.log('✅ All notifications cleared');
      } else {
        console.error('❌ Backend error clearing notifications:', response.data.error);
      }
    } catch (error) {
      console.error('❌ Error clearing notifications:', error);
      // Fallback to local state update
      setNotifications([]);
      setNotificationCount(0);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      // Call backend API to mark all as read
      const response = await api.put('/api/notifications/staff/read-all');
      
      if (response.data.success) {
        // Update local state
        setNotifications(prev => 
          prev.map(n => ({ ...n, isRead: true }))
        );
        setNotificationCount(0);
        
        console.log('✅ All notifications marked as read');
      } else {
        console.error('❌ Backend error marking all as read:', response.data.error);
      }
    } catch (error) {
      console.error('❌ Error marking all as read:', error);
      // Fallback to local state update
      setNotifications(prev => 
        prev.map(n => ({ ...n, isRead: true }))
      );
      setNotificationCount(0);
    }
  };

  // No more sample notifications - only real data from backend

  // Transform backend notifications to frontend format
  const transformBackendNotifications = (backendNotifications) => {
    return backendNotifications.map(notification => ({
      id: notification.id,
      type: notification.type,
      title: notification.title,
      message: notification.message,
      timestamp: new Date(notification.createdAt), // Convert createdAt to timestamp
      isRead: notification.isRead,
      priority: notification.priority,
      actionUrl: notification.actionUrl || '#'
    }));
  };

  // Fetch initial notification count when component mounts
  useEffect(() => {
    const fetchInitialNotifications = async () => {
      try {
        // Fetch real notifications from backend
        const response = await api.get('/api/notifications/staff');
        
        if (response.data.success) {
          const transformedNotifications = transformBackendNotifications(response.data.notifications || []);
          setNotifications(transformedNotifications);
          setNotificationCount(response.data.unreadCount || 0);
          console.log('🔔 Initial notifications loaded from backend:', transformedNotifications.length);
        } else {
          console.error('❌ Backend returned error:', response.data.error);
          setNotifications([]);
          setNotificationCount(0);
        }
      } catch (error) {
        console.error('❌ Error fetching initial notifications:', error);
        // No fallback to mock data - show empty state instead
        setNotifications([]);
        setNotificationCount(0);
        console.log('🔔 No notifications available - backend error:', error.message);
      }
    };

    fetchInitialNotifications();
  }, []);

  // Check if user is valid
  if (!user) {
    navigate('/login');
    return null;
  }

  // Check if user has staff role
  if (user.role !== 'admin' && user.role !== 'staff') {
    navigate('/login');
    return null;
  }

  const handleLogout = async () => {
    try {
      // Close any open dropdowns
      setNotificationsOpen(false);
      setSettingsOpen(false);
      
      // Call the logout function from AuthContext
      await logout();
      
      // Clear any local storage or session data
      localStorage.removeItem('token');
      localStorage.removeItem('staffPortalActiveTab');
      
      // Navigate to login page
      navigate('/staff-login');
      
      console.log('✅ Staff user logged out successfully');
    } catch (error) {
      console.error('❌ Error during logout:', error);
      // Force logout even if there's an error
      localStorage.removeItem('token');
      localStorage.removeItem('staffPortalActiveTab');
      navigate('/staff-login');
    }
  };


  const handleNotificationClick = async () => {
    console.log('🔔 Notification icon clicked!');
    try {
      if (!notificationsOpen) {
        setIsLoadingNotifications(true);
        
        // Refresh notifications from backend
        const response = await api.get('/api/notifications/staff');
        
        if (response.data.success) {
          const transformedNotifications = transformBackendNotifications(response.data.notifications || []);
          setNotifications(transformedNotifications);
          setNotificationCount(response.data.unreadCount || 0);
          console.log('🔔 Notifications refreshed from backend:', transformedNotifications.length);
        }
        
        setIsLoadingNotifications(false);
      }
      
      setNotificationsOpen(!notificationsOpen);
      console.log('🔔 Notifications toggled, open:', !notificationsOpen);
    } catch (error) {
      console.error('❌ Error fetching notifications:', error);
      setIsLoadingNotifications(false);
      setNotificationsOpen(false);
    }
  };

  const handleSettingsClick = async () => {
    console.log('⚙️ Settings icon clicked!');
    try {
      setSettingsOpen(!settingsOpen);
      
      // TODO: Fetch user settings/preferences from backend
      // Example: const userSettings = await api.get('/api/staff/profile');
      // This would populate user-specific settings data
      
      console.log('⚙️ Settings toggled, open:', !settingsOpen);
    } catch (error) {
      console.error('❌ Error fetching settings:', error);
      setSettingsOpen(false);
    }
  };


  const navigation = [
    {
      name: 'Dashboard',
      href: '/staff/dashboard',
      icon: LayoutDashboard
    },
    {
      name: 'Client Management',
      href: '/staff/manage-clients',
      icon: Users
    },

    {
      name: 'Invoice Oversight',
      href: '/staff/invoices',
      icon: FileText
    },

    {
      name: 'Staff Management',
      href: '/staff/staff',
      icon: UserCheck
    },
    {
      name: 'Security & Logs',
      href: '/staff/security',
      icon: Shield
    },
    {
      name: 'Settings',
      href: '/staff/settings',
      icon: Settings
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">


      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div 
        className={`
          fixed left-0 z-50 w-64 bg-white transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 overflow-hidden flex flex-col
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{
          height: 'calc(100% - 64px)',
          minHeight: 'calc(100% - 64px)',
          position: 'fixed',
          top: '64px',
          bottom: 0
        }}
      >
        {/* Mobile close button */}
        <button
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden absolute right-4 top-4 p-2 text-gray-600 hover:bg-gray-100 rounded-lg z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* User Info */}
        <div className="bg-white px-6 py-4">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-[#1F11ED] rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                {user?.firstName || 'Unknown'} {user?.lastName || 'User'}
              </p>
              <p className="text-xs text-gray-600">{user?.email || 'No email'}</p>
              <p className="text-xs text-gray-600">Role: {user?.role || 'Unknown'}</p>
            </div>
          </div>
          
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-2 relative z-10 bg-white">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            
            return (
              <div key={item.name} className="relative">
                <Link
                  to={item.href}
                  className={`
                    relative flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 group
                    ${isActive
                      ? 'bg-[#1F11ED] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                    }
                  `}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className={`w-5 h-5 transition-all duration-300 ${
                    isActive ? 'text-white' : 'text-gray-600 group-hover:text-gray-900'
                  }`} />
                  <span className={`transition-all duration-300 ${
                    isActive ? 'font-semibold' : 'font-medium'
                  }`}>
                    {item.name}
                  </span>
                </Link>
              </div>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="px-4 py-6 bg-white border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 w-full px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>

        {/* Dropdown Menus */}
        {/* Notifications Dropdown */}
        {notificationsOpen && (
          <div className="fixed top-16 right-6 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
              <div className="text-xs text-gray-400 mb-2">
                Debug: {notifications ? notifications.length : 'null'} notifications, 
                Loading: {isLoadingNotifications ? 'Yes' : 'No'}
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {isLoadingNotifications ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-sm text-gray-600">Loading...</span>
                </div>
              ) : notifications && notifications.length > 0 ? (
                <div className="space-y-2 p-4">
                  <p className="text-sm text-gray-600 mb-3">You have {notificationCount} unread notification{notificationCount !== 1 ? 's' : ''}</p>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {notifications && notifications.map((notification) => {
                      try {
                        if (!notification || !notification.id) {
                          console.warn('Invalid notification object:', notification);
                          return null;
                        }
                        return (
                          <div 
                            key={notification.id}
                            className={`p-3 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors duration-200 ${
                              notification.isRead ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-300'
                            }`}
                            onClick={() => {
                              markNotificationAsRead(notification.id);
                              console.log('📱 Notification clicked:', notification.title);
                              setNotificationsOpen(false);
                            }}
                          >
                            <div className="flex items-start space-x-3">
                              <div className="text-lg">{getTypeIcon(notification.type)}</div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <h4 className={`text-sm font-medium ${
                                    notification.isRead ? 'text-gray-600' : 'text-gray-900'
                                  }`}>
                                    {notification.title}
                                  </h4>
                                  <div className="flex items-center space-x-2">
                                    <span className={`px-2 py-1 text-xs rounded-full border ${getPriorityColor(notification.priority)}`}>
                                      {notification.priority}
                                    </span>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deleteNotification(notification.id);
                                      }}
                                      className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors duration-200"
                                      title="Delete notification"
                                    >
                                      🗑️
                                    </button>
                                  </div>
                                </div>
                                <p className={`text-sm ${
                                  notification.isRead ? 'text-gray-500' : 'text-gray-700'
                                } mb-2 line-clamp-2`}>
                                  {notification.message}
                                </p>
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-gray-400">
                                    {formatTimestamp(notification.timestamp)}
                                  </span>
                                  {!notification.isRead && (
                                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      } catch (error) {
                        console.error('Error rendering notification:', error, notification);
                        return (
                          <div key={`error-${Math.random()}`} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-600">Error displaying notification</p>
                          </div>
                        );
                      }
                    })}
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                    <button
                      onClick={() => {
                        markAllNotificationsAsRead();
                        setNotificationsOpen(false);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Mark all as read
                    </button>
                    <button
                      onClick={() => {
                        clearAllNotifications();
                        setNotificationsOpen(false);
                      }}
                      className="text-sm text-red-600 hover:text-red-800 font-medium"
                    >
                      Clear all
                    </button>
                  </div>
                </div>
              ) : notifications && notifications.length === 0 ? (
                <div className="p-4">
                  <p className="text-sm text-gray-600">No notifications at this time</p>
                </div>
              ) : (
                <div className="p-4 text-sm text-gray-600">
                  <p>Loading notifications...</p>
                  <p className="text-xs mt-1">If this persists, please refresh the page</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Settings Dropdown */}
        {settingsOpen && (
          <div className="fixed top-16 right-20 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
            <button 
              onClick={() => {
                navigate('/staff/profile');
                setSettingsOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200 flex items-center space-x-2"
            >
              <User className="w-4 h-4" />
              <span>Profile</span>
            </button>
            <button 
              onClick={() => {
                navigate('/staff/settings');
                setSettingsOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200 flex items-center space-x-2"
            >
              <Settings className="w-4 h-4" />
              <span>Settings</span>
            </button>
            <hr className="my-1 border-gray-200" />
            <button 
              onClick={handleLogout}
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200 flex items-center space-x-2"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="lg:pl-64 flex-1 flex flex-col overflow-hidden">
        {/* Fixed Top Bar - Full Width */}
        <header className="bg-[#1F11ED] shadow-lg border-b border-blue-300 fixed top-0 left-0 right-0 z-30">
          <div className="flex items-center justify-between h-16 px-6">
            
            {/* LEFT SIDE - Mobile Menu + Logo */}
            <div className="flex items-center space-x-4">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 text-white hover:text-white hover:bg-white/10 rounded-lg transition-colors duration-200"
              >
                <Menu className="w-5 h-5" />
              </button>
              
              {/* Logo */}
              <ReceevitLogoCompact size={120} forBlueBackground={true} />
            </div>
            
            {/* CENTER - Staff Portal Title */}
            <div className="flex items-center">
              <div className="text-base font-semibold text-white">
                Staff Portal
              </div>
            </div>
            
            {/* RIGHT SIDE - Action buttons */}
            <div className="flex items-center space-x-2">
              {/* Notifications Icon */}
              <div className="relative" ref={notificationsRef}>
                <button 
                  onClick={handleNotificationClick}
                  className="p-2 text-white hover:text-white hover:bg-white/10 rounded-lg transition-colors duration-200 relative"
                >
                  <Bell className="w-5 h-5" />
                  {notificationCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                      {notificationCount > 9 ? '9+' : notificationCount}
                    </span>
                  )}
                </button>
              </div>
              
              {/* Settings Icon */}
              <div className="relative" ref={settingsRef}>
                <button 
                  onClick={handleSettingsClick}
                  className="p-2 text-white hover:text-white hover:bg-white/10 rounded-lg transition-colors duration-200"
                >
                  <Settings className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto bg-gray-50 pt-16">
          <div className="p-6">
            <Outlet />
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-white border-t border-gray-200 mt-auto">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Company Info */}
              <div className="col-span-1 md:col-span-2">
                <div className="flex items-center space-x-4 mb-4">
                  <ReceevitLogoCompact size={160} forBlueBackground={false} />
                  <div>
                    <p className="text-sm text-gray-600">Advanced Credit Control Management</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 max-w-md">
                  Empowering businesses with intelligent credit control solutions and streamlined invoice management.
                </p>
              </div>

              {/* Quick Links */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Quick Links</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li><Link to="/staff/dashboard" className="hover:text-[#1F11ED] transition-colors">Dashboard</Link></li>
                  <li><Link to="/staff/clients" className="hover:text-[#1F11ED] transition-colors">Client Management</Link></li>
                  <li><Link to="/staff/analytics" className="hover:text-[#1F11ED] transition-colors">Analytics</Link></li>
                  <li><Link to="/staff/settings" className="hover:text-[#1F11ED] transition-colors">Settings</Link></li>
                </ul>
              </div>

              {/* Support */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Support</h3>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li><Link to="/staff/help" className="hover:text-[#1F11ED] transition-colors">Help Center</Link></li>
                  <li><Link to="/staff/documentation" className="hover:text-[#1F11ED] transition-colors">Documentation</Link></li>
                  <li><a href="mailto:receevit@outlook.com" className="hover:text-[#1F11ED] transition-colors">Contact Support</a></li>
                </ul>
              </div>
            </div>

            <div className="border-t border-gray-200 mt-8 pt-8">
              <div className="flex flex-col md:flex-row justify-between items-center">
                <p className="text-sm text-gray-600">
                  © {new Date().getFullYear()} Receevit®. All rights reserved.
                </p>
                <div className="flex flex-wrap items-center gap-4 mt-4 md:mt-0">
                  <Link to="/privacy" className="text-sm text-gray-600 hover:text-[#1F11ED] transition-colors">
                    Privacy Policy
                  </Link>
                  <Link to="/terms" className="text-sm text-gray-600 hover:text-[#1F11ED] transition-colors">
                    Terms of Use
                  </Link>
                  <Link to="/cookies" className="text-sm text-gray-600 hover:text-[#1F11ED] transition-colors">
                    Cookie Policy
                  </Link>
                  <button
                    type="button"
                    onClick={openPreferences}
                    className="text-sm text-gray-600 hover:text-[#1F11ED] transition-colors"
                  >
                    Cookie Preferences
                  </button>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default StaffLayout;
